import React from 'react';
import orheartImage from '../../orheart.webp';
import friendsImage from '../../friends.webp';
import { auraRgbToStyleVars, parseAuraRgb } from '../../utils/auraColor';
import { feedAdFindLuvIconFlags, posterFindLuvIconFlags } from '../../utils/posterFindLuv';
import { formatLookingForGendersShort, lookingForGendersForFeedAd } from './CollageDetailsLuvLegend';

function localSavedPosterForFeedAd(ad, savedPosters, currentUserId) {
  if (!currentUserId || !ad?.userId || ad.userId !== currentUserId) return null;
  const list = Array.isArray(savedPosters) ? savedPosters : [];
  return list.find((p) => p.feedAdId === ad.id || p.id === ad.id) ?? null;
}

function feedAdLuvFlags(ad, savedPosters, currentUserId) {
  const localPoster = localSavedPosterForFeedAd(ad, savedPosters, currentUserId);
  const { friend, romantic } = feedAdFindLuvIconFlags(ad, localPoster);
  return { showFriend: friend, showRomantic: romantic };
}

const FEED_SELF_GENDER_LABEL = { male: 'M', female: 'F', nb: 'NB' };

function normalizeSelfAge(age) {
  if (age == null) return null;
  const num = typeof age === 'number' ? age : Number(age);
  if (typeof num !== 'number' || !Number.isFinite(num)) return null;
  const n = Math.round(num);
  if (n < 1 || n > 150) return null;
  return String(n);
}

function feedAdSelfAgeShort(ad, savedPosters, currentUserId) {
  if (!ad) return null;
  const fromAd = normalizeSelfAge(ad.selfAge);
  if (fromAd) return fromAd;
  const localPoster = localSavedPosterForFeedAd(ad, savedPosters, currentUserId);
  return normalizeSelfAge(localPoster?.selfAge) ?? null;
}

function feedAdSelfGenderShort(ad, savedPosters, currentUserId) {
  if (!ad) return null;
  const gAd = ad.selfGender;
  if (gAd && FEED_SELF_GENDER_LABEL[gAd]) return FEED_SELF_GENDER_LABEL[gAd];
  const localPoster = localSavedPosterForFeedAd(ad, savedPosters, currentUserId);
  const g = localPoster?.selfGender;
  return g && FEED_SELF_GENDER_LABEL[g] ? FEED_SELF_GENDER_LABEL[g] : null;
}

function feedAdNicknameTrailingFlags(ad, savedPosters, currentUserId) {
  const selfGenderShort = feedAdSelfGenderShort(ad, savedPosters, currentUserId);
  const { showFriend, showRomantic } = feedAdLuvFlags(ad, savedPosters, currentUserId);
  return {
    selfGenderShort,
    showFriend,
    showRomantic,
  };
}

function posterSelfGenderShort(poster) {
  if (!poster) return null;
  const g = poster.selfGender;
  return g && FEED_SELF_GENDER_LABEL[g] ? FEED_SELF_GENDER_LABEL[g] : null;
}

function posterSelfAgeShort(poster) {
  if (!poster) return null;
  return normalizeSelfAge(poster.selfAge) ?? null;
}

/** Nickname strip: quote + gender beside quote; FIND Luv icons on the right when present. */
export function FeedNicknameTile({
  ad,
  savedPosters,
  currentUserId,
  asButton,
  onClick,
  ariaLabel,
  disabled = false,
  /** When set (e.g. saved-poster popups), use these flags instead of feed-ad FIND Luv resolution. */
  findLuvFlags,
  /** Replaces the default quoted posterNickname inside `.feed-nickname-quote` (e.g. collage editor states). */
  quoteChildren,
  /** When set (main collage editor), gender + FIND Luv icons come from this saved poster. */
  editorSavedPoster,
  /** CAL LAUNCH SPECIAL: FIVE BOUNTIES — when set, render `$amount` inline on the left of the strip. */
  bountyAmount,
  /** When true, render the profile owner's vibe aura cloud on the left (from `ad.auraColor`). */
  showVibeAura = false,
}) {
  const fromFeed = feedAdNicknameTrailingFlags(ad, savedPosters, currentUserId);
  let selfGenderShort;
  let showFriend;
  let showRomantic;
  let selfAgeShort;
  if (editorSavedPoster) {
    selfGenderShort = posterSelfGenderShort(editorSavedPoster);
    selfAgeShort = posterSelfAgeShort(editorSavedPoster);
    const fl = posterFindLuvIconFlags(editorSavedPoster);
    showFriend = fl.friend;
    showRomantic = fl.romantic;
  } else {
    selfGenderShort = fromFeed.selfGenderShort;
    selfAgeShort = feedAdSelfAgeShort(ad, savedPosters, currentUserId);
    showFriend = findLuvFlags != null ? !!findLuvFlags.friend : fromFeed.showFriend;
    showRomantic = findLuvFlags != null ? !!findLuvFlags.romantic : fromFeed.showRomantic;
  }
  const selfGenderWithAgeText =
    selfGenderShort
      ? (selfAgeShort ? `${selfGenderShort} ${selfAgeShort}` : selfGenderShort)
      : null;
  const quote = quoteChildren != null ? quoteChildren : `"${ad.posterNickname ?? 'The One'}"`;
  const hasLuvIcons = showFriend || showRomantic;
  const hasBounty = typeof bountyAmount === 'number' && Number.isFinite(bountyAmount);
  const auraRgb = showVibeAura ? parseAuraRgb(ad?.auraColor) : null;
  const auraStyle = auraRgb ? auraRgbToStyleVars(auraRgb) : null;
  const hasAura = !!auraStyle;
  const hasLeftCluster = hasAura || hasBounty;
  const lookingForGenders = editorSavedPoster
    ? (Array.isArray(editorSavedPoster.lookingForGenders) && editorSavedPoster.lookingForGenders.length
      ? editorSavedPoster.lookingForGenders
      : null)
    : lookingForGendersForFeedAd(ad, savedPosters, currentUserId) ?? null;
  const romanticMatchGendersLabel = showRomantic
    ? formatLookingForGendersShort(lookingForGenders)
    : null;
  const luvIcons = hasLuvIcons ? (
    <span className="feed-nickname-luv-icons">
      {showRomantic ? (
        <span className="feed-nickname-luv-romantic-wrap">
          <img src={orheartImage} alt="" className="feed-nickname-luv-icon feed-nickname-luv-icon--romantic" />
          {romanticMatchGendersLabel ? (
            <span className="feed-nickname-luv-romantic-letters" aria-hidden="true">
              {romanticMatchGendersLabel}
            </span>
          ) : null}
        </span>
      ) : null}
      {showFriend && showRomantic ? (
        <span className="feed-nickname-luv-divider" aria-hidden="true">|</span>
      ) : null}
      {showFriend ? (
        <img src={friendsImage} alt="" className="feed-nickname-luv-icon feed-nickname-luv-icon--friends" />
      ) : null}
    </span>
  ) : null;
  const leftCluster = hasLeftCluster ? (
    <span className="feed-nickname-left">
      {hasAura ? (
        <span className="feed-nickname-aura" style={auraStyle} aria-hidden="true">
          <span className="hero-aura-cloud__blob hero-aura-cloud__blob--one" />
          <span className="hero-aura-cloud__blob hero-aura-cloud__blob--two" />
          <span className="hero-aura-cloud__blob hero-aura-cloud__blob--three" />
        </span>
      ) : null}
      {hasBounty ? (
        <span className="feed-nickname-bounty">{`$${bountyAmount}`}</span>
      ) : null}
    </span>
  ) : null;
  const tileClass = `the-one-label the-one-label-feed feed-nickname-tile${hasLuvIcons ? ' feed-nickname-tile--has-luv-icons' : ''}${hasBounty ? ' feed-nickname-tile--has-bounty' : ''}${hasAura ? ' feed-nickname-tile--has-aura' : ''}${hasLeftCluster ? ' feed-nickname-tile--has-left' : ''}`;
  const leading = (
    <span className="feed-nickname-leading">
      <span className="feed-nickname-quote">{quote}</span>
      {selfGenderWithAgeText ? <span className="feed-nickname-self-gender">{selfGenderWithAgeText}</span> : null}
    </span>
  );
  if (asButton) {
    return (
      <button
        type="button"
        className={`${tileClass} the-one-label-clickable`}
        onClick={onClick}
        aria-label={ariaLabel}
        disabled={disabled}
      >
        {leftCluster}
        {leading}
        {luvIcons}
      </button>
    );
  }
  return (
    <p className={tileClass} aria-hidden="true">
      {leftCluster}
      {leading}
      {luvIcons}
    </p>
  );
}
